import classNames from 'classnames';
import { Document } from 'react-pdf';
import { useEffect, useState } from 'react';
import { useDebouncedState } from '../../../hooks/useDebouncedState';
import SetupPDFWorker from '../../../utils/PDFWorker';
import * as styles from './pdfViewerBase.linaria';
import PdfPage from './PdfPage/PdfPage';
import PDFNotification from './PdfNotification';
SetupPDFWorker();
/**
 * A base component for displaying PDFs, most likely should not be used alone, but can be implemented
 * in other components that needs PDF rendering.
 * Has the option to only display a single page (with navigation being managed outside) or to use infinite scroll
 * where the pages will load dynamically based on the user scrolling through the document.
 */
function PdfViewerBase({ className, file, onLoadSuccess, onLoadError, useInfiniteScroll, currentPageNumber, pagesToRender, width, height, scale, }) {
    const [pdfInfo, setPdfInfo] = useState(undefined);
    /**
     * We debounce the current focus so that we reduce how often the pages render when the user is scrolling through
     */
    const [currentFocus, , setCurrentFocus] = useDebouncedState(1, 250);
    const [defaultHeight, setDefaultHeight] = useState(undefined);
    const [firstPageRendered, setFirstPageRendered] = useState(false);
    const [pages, setPages] = useState([]);
    const onLoaded = (pdf) => {
        setPdfInfo(pdf);
        if (onLoadSuccess) {
            onLoadSuccess(pdf);
        }
    };
    const { numPages } = pdfInfo || {};
    const onPageFocused = (newPageNumber) => {
        if (useInfiniteScroll) {
            setCurrentFocus(newPageNumber);
        }
    };
    const onPageRender = (pdfPage) => {
        if (!firstPageRendered) {
            setFirstPageRendered(true);
        }
        /**
         * We make a naive assumption that each page has the same height
         * as the previous page. The page will update itself with the real height
         * so it's not a big deal if this is not true
         */
        if (pdfPage.height !== defaultHeight) {
            setDefaultHeight(pdfPage.height);
        }
    };
    useEffect(() => {
        if (useInfiniteScroll) {
            const newPages = [];
            const totalPages = firstPageRendered ? numPages : 1;
            for (let pageNum = 1; pageNum <= totalPages; pageNum += 1) {
                newPages.push(<PdfPage height={height} width={width} scale={scale} currentFocus={currentFocus} pagesToRender={pagesToRender} onPageFocused={onPageFocused} key={pageNum} pageNumber={pageNum} onRenderSuccess={onPageRender} defaultPageHeight={defaultHeight} inifiniteScrollActive/>);
            }
            setPages(newPages);
        }
    }, [currentFocus, firstPageRendered, numPages, useInfiniteScroll, width, height, scale]);
    const loader = <PDFNotification />;
    const noData = <PDFNotification text="Kan ikke laste PDF"/>;
    return (<div className={classNames([styles.style, className, 'hj-pdf-container'])}>
      <Document file={file} noData={noData} error={noData} onLoadSuccess={onLoaded} onLoadError={onLoadError} loading={loader}>
        {useInfiniteScroll && pages}
        {!useInfiniteScroll && (<PdfPage height={height} width={width} scale={scale} key={currentPageNumber} pageNumber={currentPageNumber}/>)}
      </Document>
    </div>);
}
PdfViewerBase.defaultProps = {
    className: null,
    file: null,
    onLoadSuccess: null,
    useInfiniteScroll: false,
    onLoadError: undefined,
    pageNumber: 1,
    pagesToRender: 4,
    width: undefined,
    height: undefined,
    scale: undefined,
};
export default PdfViewerBase;
